import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import { useRealtimeUnits } from 'Core/hooks';
import {createMetricService, reset, updateMetricService,} from "./redux/slice";
import Modal, {ModalBody, ModalFooter} from "Components/Modal";
import {Button, Form, Select} from 'Components';
import {useIntl} from "react-intl";
import {NotificationManager} from "../../../../../../components/Notifications";

//Styles
import './styles.scss'
import colors from "Assets/sass/core/vars.scss";
import {
    CATEGORY_ADVANCED,
    CATEGORY_DRIVER,
    COLOR_BLACK,
    COLOR_EVT_OTHER,
    METRIC_SPEED_OVER_LABEL_HIGH,
    METRIC_SPEED_OVER_LABEL_LIST,
    METRIC_SPEED_OVER_LABEL_LOW,
    METRIC_SPEED_OVER_LABEL_MEDIUM,
    METRIC_VALUE_SPEED_LIST,
    resolveBehaviorMetricId,
    useMetricTypesOptions
} from "Modules/settings/views/behavior/redux/helpers";
import Slider from "Components/Slider";
import {convertToKPH} from "Core/data/Helpers";

const CreateUpdateMetric = (props) => {
    const {refresh} = props;
    const dispatch = useDispatch();
    const {messages, locale} = useIntl();
    const {
        metric,
        loading,
        error,
        createMetricResult,
        updateMetricResult,
        open
    } = useSelector((state) => state.manageCreateUpdateMetricServiceRedux);

    const {user: {isKph}} = useSelector((state) => state.securityRedux);
    const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags', 'manufacturerName', 'linkedUnit', 'videoDevices'] });

    const {metricTypesOptions, getNameMetricType, getMetricType} = useMetricTypesOptions(isKph, messages);
    const formSetup = useRef()
    const [metricValueSpeedList, setMetricValueSpeedList] = useState(METRIC_VALUE_SPEED_LIST)
    const buildInitialFormData = (currentMetric) => ({
        metric: getMetricType(currentMetric),
        points: currentMetric?.points || 0,
        metricValue: {
            value: currentMetric?.metricValue ?? undefined,
            name: currentMetric?.metricValue !== undefined && currentMetric?.metricValue !== null
                ? (isKph ? convertToKPH(currentMetric.metricValue) : currentMetric.metricValue)
                : undefined,
        },
        id: currentMetric?.id
    });
    const [formData, setFormData] = useState(() => buildInitialFormData(metric));

    const allMetricOptions = useMemo(() => {
            const hasVidFleet = units.some((data) => {
                return [
                    data?.manufacturerName,
                    data?.linkedUnit?.manufacturerName,
                    data?.videoDevices?.[0]?.manufacturer,
                ].some((manufacturerName) => manufacturerName?.toUpperCase() === 'VIDFLEET')
            });

            if (hasVidFleet) {
                return metricTypesOptions;
            }

            const filteredOptions = metricTypesOptions.filter(category => ![CATEGORY_DRIVER, CATEGORY_ADVANCED].includes(category.id));
            const resolvedMetricId = resolveBehaviorMetricId(metric?.metric);
            const selectedCategory = metricTypesOptions.find((category) => category.id === metric?.category);
            const selectedOption = selectedCategory?.options?.find((option) => option.id === resolvedMetricId);

            if (!selectedCategory || !selectedOption || filteredOptions.some((category) => category.id === selectedCategory.id)) {
                return filteredOptions;
            }

            return [
                ...filteredOptions,
                {
                    ...selectedCategory,
                    options: [selectedOption],
                },
            ];
        }, [metric, metricTypesOptions, units]
    );
    const title = useMemo(() => {
            return formData?.metric?.id && metric
                ? `${messages["createBehavior_editMetric"]} ${getNameMetricType(metric)}`
                : messages["createBehavior_newMetric"]
        }, [formData?.metric?.id, getNameMetricType, messages, metric]
    );

    const [showErrorValue, setShowErrorValue] = useState(false);

    const handleClose = useCallback(() => {
        dispatch(reset({
            open: false,
            createMetricResult: null,
            updateMetricResult: null
        }));
    }, []);

    const handleSuccess = useCallback((message) => {
        NotificationManager.success(
            messages[message],
            messages["success"],
            3000,
            null,
            null,
            "success"
        );
        refresh();
        handleClose();
    }, []);

    useEffect(() => {
        if (createMetricResult || updateMetricResult) {
            handleSuccess(`createBehavior_${createMetricResult ? 'createMetric' : 'updateMetric'}_success`);
        }
    }, [createMetricResult, updateMetricResult]);

    useEffect(() => {
        if (error) {
            const errorMessage = error?.error === 'This metric has already been created.' ? messages['behavior_metricHasAlready'] : error?.error;
            NotificationManager.error(
                errorMessage || messages["errorMessage"],
                messages["error"],
                3000,
                null,
                null,
                "error"
            );
            dispatch(reset({error: null}));
        }
    }, [error]);


    useEffect(() => {
        setMetricValueSpeedList([... METRIC_VALUE_SPEED_LIST.map(speed => {
            return {name: isKph ? convertToKPH(speed.value) : speed.value, value: speed.value};
        }), {name: '--', value: undefined}]);
    }, [isKph]);

    const formatGroupLabel = (data) => (
        <>
            <div style={{
                borderTop: `1px solid ${colors[COLOR_EVT_OTHER]}`,
                width: '100%'
            }}>
            </div>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px',
                paddingTop: '10px'
            }}>
                <span style={{
                    fontWeight: 'bold',
                    color: colors[COLOR_BLACK],
                    fontSize: '12px'
                }}>{data?.label}
                </span>
            </div>
        </>
    );

    return (
        <Modal
            title={
                title && title.length > 48 ? title.substring(0, 45) + '..."' : title
            }
            open={open}
            handleClose={handleClose}
            size="extra-small"
            position="right"
            className="modal-behavior-list-filter"
        >
            <ModalBody className="modal-body-behavior-list-filter">
                <div style={{paddingLeft: 25, paddingRight: 25}}>
                    <Form
                        ref={formSetup}
                        loading={loading}
                        onValidSubmit={() => {
                            let metricValue = formData.metricValue?.value
                            if (METRIC_SPEED_OVER_LABEL_LIST.includes(formData.metric.id)) {
                                if (formData.metric.id === METRIC_SPEED_OVER_LABEL_LOW) {
                                    metricValue = 5
                                } else if (formData.metric.id === METRIC_SPEED_OVER_LABEL_MEDIUM) {
                                    metricValue = 10
                                } else if (formData.metric.id === METRIC_SPEED_OVER_LABEL_HIGH) {
                                    metricValue = 15
                                }
                            }

                            if(metric?.id) {
                                dispatch(updateMetricService({
                                    ...formData,
                                    metricValue: {value: metricValue}
                                }))
                            } else {
                                dispatch(createMetricService({
                                    ...formData,
                                    metricValue: {value: metricValue}
                                }))
                            }
                        }}
                        onSubmit={() => {
                            if (formData?.metric?.id === 'speedLabel') {
                                const result = formData?.metricValue?.value !== undefined
                                setShowErrorValue(!result)
                                return result
                            } else {
                                return formData.metric !== undefined
                            }
                        }}
                    >
                        <Select
                            id="metricType"
                            label={messages['behavior_metric']}
                            value={formData.metric}
                            onChange={(value) => {
                                setFormData({...formData, metric: value});
                            }}
                            options={allMetricOptions}
                            textField="name"
                            valueField="id"
                            placeholder={messages['behavior_metric']}
                            isClearable
                            required
                            requiredMessage={messages["createBehavior_selectAMetricOption"]}
                            isOptionDisabled={(option) => {
                                return !option || option.disabled
                            }}
                            className={'selected-behavior-options'}
                            formatGroupLabel={formatGroupLabel}
                            titleStyles={'behavior-setup-type-title'}
                            styleIcon={{color: colors[COLOR_BLACK]}}
                            messageInfoIcon={messages["generatedReport_infoReports"]}
                        />
                        {
                            formData?.metric?.id === 'speedLabel' &&
                            <>
                            <div className='modal-option-value-speed'>
                                <p className='modal-create-label-slider'>
                                    {messages['behavior_speedLabel']}
                                </p>
                                <div style={{padding: 5}}>
                                    <Select
                                        id="search-fsm-messages"
                                        value={formData?.metricValue?.name ? formData?.metricValue : {name: '--', value: undefined}}
                                        options={metricValueSpeedList}
                                        textField="name"
                                        valueField="value"
                                        onChange={(value) => {
                                            setFormData({...formData, metricValue: value})
                                        }}
                                        placeholder={`--`}
                                    />
                                </div>
                                <p className='modal-create-label-slider-kph'>
                                    <strong>{isKph ? 'KPH' : 'MPH'}</strong>
                                </p>
                            </div>
                                {showErrorValue && <small style={{padding: 5}} className="select-error text-danger">{messages['createBehavior_metricValueRequired']}</small>}
                            </>
                        }
                        <div>
                            <p className='modal-create-label-slider-down'>
                                {messages['behavior_riskFactorsPoints']}
                            </p>
                            <div style={{padding: 15}}>
                                <Slider
                                    style={{paddingLeft: 25}}
                                    min={1}
                                    max={10}
                                    value={formData?.points || 1}
                                    onChange={(value) => setFormData({...formData, points: value})}
                                    showCenterValue
                                />
                            </div>
                        </div>
                    </Form>
                </div>
            </ModalBody>
            <ModalFooter>
                <div style={{display: "flex", alignItems: "center", width: "100%"}}>
                    <Button
                        isDisabled={loading}
                        style={{marginRight: 8}}
                        onClick={handleClose}
                        className="btn-cancel"
                    >
                        {messages["cancel"]}
                    </Button>

                    <Button
                        isDisabled={loading}
                        onClick={() => {
                            formSetup.current.submit();
                        }}
                        className='btn-blue'
                    >
                        {metric?.id ? messages["update"] : messages["add"]}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default CreateUpdateMetric
